import { defineComponent as _defineComponent } from 'vue'
import { PropType } from 'vue'
import { computed, useContext } from '@nuxtjs/composition-api'
import { useOrderModel } from '@entities/order'
import { useLoyaltyModel } from '@entities/loyalty'
import { useRestaurantModel } from '@entities/restaurant'
import { useMetaModel } from '@entities/meta'
import { useBrowserModel } from '@entities/browser'
import { Cookie } from '@shared/lib'


export default /*#__PURE__*/_defineComponent({
  __name: 'BonusButton',
  props: {
  numberBonuses: {
    type: Number as PropType<number | null>,
    required: false,
    default: null
  },
  appliedBonuses: {
    type: Number as PropType<number | null>,
    required: false,
    default: null
  }
},
  emits: [
  'handleClick',
  'toggleHasPrimeHillDiscount',
  'togglePrimeHillButton',
  'callYandexMetricGoal'
],
  setup(__props, { emit: emits }) {

const props = __props





const orderModel = useOrderModel()
const loyaltyModel = useLoyaltyModel()
const restaurantModel = useRestaurantModel()
const metaModel = useMetaModel()
const browserModel = useBrowserModel()
const { route } = useContext()
const VERIFIED_PHONE_COOKIE = 'primeHillCodeVerifiedPhone'

const isVerificationOn = (value: unknown): boolean => {
  if (value === true || value === 1) {
    return true
  }
  if (value === false || value === 0 || value === null || value === undefined) {
    return false
  }

  return ['ON', 'TRUE', '1', 'YES', 'Y'].includes(String(value).trim().toUpperCase())
}

const normalizeBarcode = (value: unknown): string => String(value ?? '').trim()
const normalizePhone = (value: unknown): string => String(value ?? '').replace(/\D/g, '')

const modalName = computed(() => {
  const isVerificationEnabled =
    isVerificationOn(loyaltyModel.verification) ||
    isVerificationOn(orderModel.orderData?.global_system_loyalty?.verification)
  const currentPhone = normalizePhone(Cookie.get('primeHillPhone'))
  const verifiedPhone = normalizePhone(Cookie.get(VERIFIED_PHONE_COOKIE))
  const shouldShowCodeStep = isVerificationEnabled && currentPhone.length > 0 && currentPhone !== verifiedPhone
  if (orderModel.orderData && orderModel.orderData.global_system_loyalty?.application_loyalty) {
    const primeHIllPhone = normalizePhone(Cookie.get('primeHillPhone'))
    const loyaltyPhone = normalizePhone(orderModel.orderData.global_system_loyalty?.phone)
    if (!primeHIllPhone.length) {
      return 'phoneNumberEntry'
    }
    if (!loyaltyPhone.length) {
      return loyaltyModel.userNameGreeting.length
        ? (shouldShowCodeStep ? 'phoneNumberEntry' : 'writeOffBonuses')
        : 'phoneNumberEntry'
    }
    return primeHIllPhone === loyaltyPhone
      ? 'bonusInfo'
      : (shouldShowCodeStep ? 'phoneNumberEntry' : 'writeOffBonuses')
  }
  if (loyaltyModel.userNameGreeting.length) {
    return shouldShowCodeStep ? 'phoneNumberEntry' : 'writeOffBonuses'
  }
  return 'phoneNumberEntry'
})

const hasBonusConflict = computed(() => {
  const orderBonus = Number(props.appliedBonuses ?? orderModel.orderData?.bonus ?? 0)
  const isApplicationLoyalty = Boolean(orderModel.orderData?.global_system_loyalty?.application_loyalty)
  const orderBarcode = normalizeBarcode(orderModel.orderData?.global_system_loyalty?.barcode)
  const userBarcode = normalizeBarcode(loyaltyModel.loyaltyBarcode)
  const orderPhone = normalizePhone(orderModel.orderData?.global_system_loyalty?.phone)
  const currentDevicePhone = normalizePhone(loyaltyModel.currentPhone || Cookie.get('primeHillPhone'))
  const isSameBarcode = orderBarcode.length > 0 && userBarcode.length > 0 && orderBarcode === userBarcode
  const isSamePhone = orderPhone.length > 0 && currentDevicePhone.length > 0 && orderPhone === currentDevicePhone
  const isAuthorizedOwner = isSameBarcode || isSamePhone

  // Пока loyalty еще не применялась к заказу, не блокируем вход в flow регистрации.
  if (!isApplicationLoyalty) {
    return orderBonus > 0
  }

  // Если barcode уже записан в заказе, пускаем в бонусы только владельца этой авторизации.
  const barcodeLockedByAnotherUser = orderBarcode.length > 0 && !isAuthorizedOwner
  const phoneLockedByAnotherUser = orderBarcode.length === 0 && orderPhone.length > 0 && !isSamePhone

  return orderBonus > 0 || barcodeLockedByAnotherUser || phoneLockedByAnotherUser
})

const refreshOrderForBonusLock = async () => {
  if (!orderModel.orderData || !restaurantModel.code || !route.value?.params?.id) {
    return
  }

  try {
    await orderModel.fetchOrder({
      tableId: route.value.params.id,
      domen: restaurantModel.code,
      session: null,
      guest: metaModel.guest ? Number(metaModel.guest) : null,
      browserId: browserModel.browserId,
      orderGuid: route.value.query.order_guid
    })
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log(e)
  }
}

const hasVerifiedLoyaltyAccess = computed(() => {
  const isVerificationEnabled =
    isVerificationOn(loyaltyModel.verification) ||
    isVerificationOn(orderModel.orderData?.global_system_loyalty?.verification)
  const currentPhone = loyaltyModel.currentPhone || String(Cookie.get('primeHillPhone') ?? '')
  const verifiedPhone = loyaltyModel.verifiedPhone || String(Cookie.get(VERIFIED_PHONE_COOKIE) ?? '')

  if (!isVerificationEnabled) {
    return loyaltyModel.userNameGreeting.length > 0
  }

  return loyaltyModel.userNameGreeting.length > 0 && currentPhone.length > 0 && currentPhone === verifiedPhone
})

const handleClick = async () => {
  if (!loyaltyModel.userNameGreeting.length) {
    emits('callYandexMetricGoal', 'PHApplyBonus') // callYandexMetricGoal
  }
  const targetModal = modalName.value
  if (targetModal === 'bonusInfo') {
    emits('handleClick', targetModal)
    return
  }

  await refreshOrderForBonusLock()
  if (orderModel.orderData && hasBonusConflict.value) {
    Cookie.set('hidenPrimeHillButton', orderModel.orderData.session ?? '')
    emits('togglePrimeHillButton')
    emits('toggleHasPrimeHillDiscount')
  } else {
    emits('handleClick', targetModal)
  }
}

const buttonStyleLoyalty = computed(() => {
  const loyaltyCode = orderModel.orderData?.global_system_loyalty?.code
  if (loyaltyCode === 'prime_hill') {
    return { border: '1px solid #6764FF' }
  } else if (loyaltyCode === 'iiko_card') {
    return { border: '1px solid #FF6E65' }
  } else {
    return {} // по умолчанию без стиля
  }
})

const loyaltyFillColor = computed(() => {
  const loyaltyCode = orderModel.orderData?.global_system_loyalty?.code
  if (loyaltyCode === 'prime_hill') {
    return '#6764FF'
  } else if (loyaltyCode === 'iiko_card') {
    return '#FF6E65'
  } else {
    return '#6764FF' // значение по умолчанию
  }
})

const baseBonusButtonAmountColor = computed(() => {
  const loyaltyCode = orderModel.orderData?.global_system_loyalty?.code
  if (loyaltyCode === 'prime_hill') {
    return { color: '#6764FF' }
  } else if (loyaltyCode === 'iiko_card') {
    return { color: '#FF6E65' }
  } else {
    return {}
  }
})

return { __sfc: true,props, emits, orderModel, loyaltyModel, restaurantModel, metaModel, browserModel, route, VERIFIED_PHONE_COOKIE, isVerificationOn, normalizeBarcode, normalizePhone, modalName, hasBonusConflict, refreshOrderForBonusLock, hasVerifiedLoyaltyAccess, handleClick, buttonStyleLoyalty, loyaltyFillColor, baseBonusButtonAmountColor }
}

})